import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Tabs,
    Tab,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Receipt as ReceiptIcon,
    Settings as SettingsIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import api, { payrollAPI, staffAPI } from '../services/api';
import { toast } from 'react-toastify';

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`payroll-tabpanel-${index}`}
            aria-labelledby={`payroll-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const Payroll = () => {
    const [tabValue, setTabValue] = useState(0);
    const [salaryStructures, setSalaryStructures] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [salarySlips, setSalarySlips] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState(''); // 'structure', 'generate'
    const [formData, setFormData] = useState({});

    const fetchData = async () => {
        try {
            const [structuresRes, staffRes, slipsRes] = await Promise.all([
                payrollAPI.getStructures(),
                staffAPI.getAll(),
                payrollAPI.getSlips()
            ]);
            setSalaryStructures(structuresRes.data.data);
            setStaffList(staffRes.data.data);
            setSalarySlips(slipsRes.data.data);
        } catch (error) {
            console.error('Error fetching payroll data:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenDialog = (type) => {
        setDialogType(type);
        setFormData({});
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        try {
            if (dialogType === 'structure') {
                await payrollAPI.createStructure(formData);
            } else if (dialogType === 'generate') {
                await payrollAPI.generateSlip(formData);
            }
            toast.success(dialogType === 'structure' ? 'Salary Structure Saved' : 'Payslip Generated');

            setOpenDialog(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const calculateNetSalary = (structure) => {
        const basic = Number(structure.basicSalary) || 0;
        const hra = Number(structure.hra) || 0;
        const allowances = Number(structure.allowances) || 0;
        const deductions = Number(structure.deductions) || 0;
        return basic + hra + allowances - deductions;
    };

    const printSlip = (slip) => {
        // Open a new window with the download URL
        window.open(`${api.defaults.baseURL}/payroll/slips/${slip.id}/download`, '_blank');
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Payroll Management
            </Typography>

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab icon={<SettingsIcon />} label="Salary Structure" />
                    <Tab icon={<ReceiptIcon />} label="Salary Slips" />
                </Tabs>

                {/* Salary Structure Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog('structure')} sx={{ mb: 2 }}>
                        Define Structure
                    </Button>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Staff Name</TableCell>
                                    <TableCell>Basic Salary</TableCell>
                                    <TableCell>HRA</TableCell>
                                    <TableCell>Allowances</TableCell>
                                    <TableCell>Deductions</TableCell>
                                    <TableCell>Net Salary</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {salaryStructures.map((struct) => (
                                    <TableRow key={struct.id}>
                                        <TableCell>{struct.staff?.firstName} {struct.staff?.lastName}</TableCell>
                                        <TableCell>{struct.basicSalary}</TableCell>
                                        <TableCell>{struct.hra}</TableCell>
                                        <TableCell>{struct.allowances}</TableCell>
                                        <TableCell>{struct.deductions}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            {calculateNetSalary(struct)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Salary Slips Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog('generate')} sx={{ mb: 2 }}>
                        Generate Payslip
                    </Button>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Staff</TableCell>
                                    <TableCell>Month/Year</TableCell>
                                    <TableCell>Total Earnings</TableCell>
                                    <TableCell>Total Deductions</TableCell>
                                    <TableCell>Net Salary</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {salarySlips.map((slip) => (
                                    <TableRow key={slip.id}>
                                        <TableCell>{new Date(slip.generatedDate).toLocaleDateString()}</TableCell>
                                        <TableCell>{slip.staff?.firstName} {slip.staff?.lastName}</TableCell>
                                        <TableCell>{slip.month}/{slip.year}</TableCell>
                                        <TableCell>{slip.totalEarnings}</TableCell>
                                        <TableCell>{slip.totalDeductions}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{slip.netSalary}</TableCell>
                                        <TableCell>
                                            <Button startIcon={<PrintIcon />} size="small" onClick={() => printSlip(slip)}>
                                                Print
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>
            </Paper>

            {/* Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialogType === 'structure' ? 'Define Salary Structure' : 'Generate Payslip'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

                        <TextField select label="Select Staff" fullWidth value={formData.staffId || ''} onChange={(e) => setFormData({ ...formData, staffId: e.target.value })}>
                            {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.employeeId})</MenuItem>)}
                        </TextField>

                        {dialogType === 'structure' && (
                            <>
                                <TextField label="Basic Salary" type="number" fullWidth value={formData.basicSalary || ''} onChange={(e) => setFormData({ ...formData, basicSalary: e.target.value })} />
                                <TextField label="HRA" type="number" fullWidth value={formData.hra || ''} onChange={(e) => setFormData({ ...formData, hra: e.target.value })} />
                                <TextField label="Allowances" type="number" fullWidth value={formData.allowances || ''} onChange={(e) => setFormData({ ...formData, allowances: e.target.value })} />
                                <TextField label="Deductions" type="number" fullWidth value={formData.deductions || ''} onChange={(e) => setFormData({ ...formData, deductions: e.target.value })} />
                            </>
                        )}

                        {dialogType === 'generate' && (
                            <Stack direction="row" spacing={2}>
                                <TextField select label="Month" fullWidth value={formData.month || ''} onChange={(e) => setFormData({ ...formData, month: e.target.value })}>
                                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m =>
                                        <MenuItem key={m} value={m}>{m}</MenuItem>
                                    )}
                                </TextField>
                                <TextField label="Year" type="number" fullWidth value={formData.year || new Date().getFullYear()} onChange={(e) => setFormData({ ...formData, year: e.target.value })} />
                            </Stack>
                        )}

                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {dialogType === 'structure' ? 'Save Structure' : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Payroll;
